import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Rechnung, RECHNUNG_STATUS, Rechnungseinstellungen } from '../types';
import rechnungService, { rechnungseinstellungenService } from '../services/rechnungService';
import SchnellrechnungModal from '../components/SchnellrechnungModal';
import RechnungsZeitachse from '../components/RechnungsZeitachse';
import { markiereNeu } from '../utils/ankunft';
import { useStueckweise } from '../utils/stueckweise';
import {
  useVorgemerkteRechnungen, vorschauGemerkt, VorschauSchalter, VorschauKopf, Zeitraum, ZeitraumAuswahl,
} from '../components/VorgemerkteRechnungen';
import { useAuth } from '../contexts/AuthContext';
import {
  Button, Card, PageHeader, LoadingScreen, EmptyState, Icon,
  Select, inputClass,
  FilterStand, filterAktiv,
} from '../components/ui';
import { RECHNUNG_STATUS_LABEL } from '../config/rechnungStatus';
import { formatBetrag } from '../utils/preis';


/**
 * Rechnungsübersicht. Die Entwürfe stehen oben – sie sind die eigentliche Arbeitsliste, alles
 * andere ist Historie.
 */
const Rechnungen: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [items, setItems] = useState<Rechnung[]>([]);
  const [einstellungen, setEinstellungen] = useState<Rechnungseinstellungen | null>(null);
  const [loading, setLoading] = useState(true);
  // Vorbelegung über die Adresse, damit das Dashboard direkt in die passende Liste springt:
  // ?status=ENTWURF | UEBERFAELLIG (kein echter Status, sondern gestellt + fällig überschritten), ?vorschau=1.
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get('status') ?? '');
  const [jahr, setJahr] = useState('');
  const [suche, setSuche] = useState('');
  const [schnell, setSchnell] = useState(false);
  const [bezahltLaeuft, setBezahltLaeuft] = useState<number | null>(null);
  const [vorschau, setVorschau] = useState(() => searchParams.get('vorschau') === '1' || vorschauGemerkt());
  const [zeitraum, setZeitraum] = useState<Zeitraum>('monat');
  const vorgemerkt = useVorgemerkteRechnungen(vorschau, zeitraum);

  /** Direkt aus der Liste als bezahlt vermerken – ohne die Rechnung zu öffnen. */
  const alsBezahlt = async (r: Rechnung) => {
    setBezahltLaeuft(r.id);
    try {
      const neu = await rechnungService.bezahlt(r.id);
      markiereNeu(`rechnung-${r.id}`);
      setItems(liste => liste.map(x => (x.id === r.id ? neu : x)));
      toast.success(`${r.rechnungsnummer ?? 'Rechnung'} als bezahlt vermerkt`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Rechnung konnte nicht als bezahlt vermerkt werden');
    } finally {
      setBezahltLaeuft(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [r, e] = await Promise.all([
        rechnungService.getAll(),
        rechnungseinstellungenService.get().catch(() => null),
      ]);
      setItems(r);
      setEinstellungen(e);
    } catch { toast.error('Rechnungen konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return items.filter(r => {
      if (status === 'UEBERFAELLIG') { if (!r.ueberfaellig) return false; }
      else if (status && r.status !== status) return false;
      if (jahr && String(r.abrechnungsjahr) !== jahr) return false;
      if (!q) return true;
      return `${r.rechnungsnummer ?? ''} ${r.kundeName ?? ''} ${r.kundennummer ?? ''}`
        .toLowerCase().includes(q);
    });
  }, [items, status, jahr, suche]);

  // Vorgemerkte haben keinen Status – ein Statusfilter blendet sie aus; Suche und Jahr gelten auch für sie.
  const vorgemerktGefiltert = useMemo(() => {
    if (status) return [];
    const q = suche.trim().toLowerCase();
    return vorgemerkt.rechnungen.filter(r =>
      (!jahr || String(r.abrechnungsjahr) === jahr)
      && (!q || `${r.kundeName ?? ''} ${r.vertragsnummer ?? ''}`.toLowerCase().includes(q)));
  }, [vorgemerkt.rechnungen, status, jahr, suche]);

  // Kein Blättern: die Zeitachse läuft durch und lädt beim Scrollen still nach.
  const { sichtbar, Ende } = useStueckweise(gefiltert);

  const jahre = useMemo(
    () => Array.from(new Set(items.map(r => r.abrechnungsjahr))).sort((a, b) => b - a),
    [items],
  );

  const offeneSumme = useMemo(
    // Über alle Rechnungen, nicht die gefilterten – „offen“ im Kopf darf nicht vom Filter abhängen.
    () => items.filter(r => r.status === 'GESTELLT')
      .reduce((s, r) => s + r.summeBrutto, 0),
    [items],
  );

  if (loading) return <LoadingScreen />;

  const fehltNoch = einstellungen?.fehlendePflichtangaben ?? [];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <PageHeader
        title="Rechnungen"
        subtitle={`${items.length} Rechnungen · ${formatBetrag(offeneSumme)} offen`}
        actions={
          <Button onClick={() => setSchnell(true)} icon={<Icon.Plus className="h-4 w-4" />}>Schnellrechnung</Button>
        }
      />

      {schnell && <SchnellrechnungModal onClose={() => setSchnell(false)} />}

      {/* Ohne vollständige Rechnungsdaten lässt sich keine Rechnung stellen – früh sagen, nicht
          erst beim Klick auf "Stellen". */}
      {fehltNoch.length > 0 && (
        <Card className="mb-4 border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">
            <strong>Rechnungsdaten unvollständig.</strong> Solange {fehltNoch.join(', ')} fehlt,
            lässt sich keine Rechnung stellen.
            {isAdmin() && (
              <> <button onClick={() => navigate('/rechnungsdaten')} className="font-semibold underline">
                Jetzt ergänzen
              </button></>
            )}
          </p>
        </Card>
      )}

      {/* Auf dem iPad im Hochformat: Suche über die ganze Breite, Status/Jahr nebeneinander, darunter
          abgesetzt Vorschau und Trefferzahl. Ab xl passt alles in eine Zeile. */}
      <Card className="mb-4 space-y-3 p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap xl:flex-nowrap">
          <div className="relative min-w-0 flex-1 sm:basis-full xl:basis-auto">
            <span className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${suche.trim() ? 'text-brand-600' : 'text-gray-400'}`}><Icon.Search className="h-4 w-4" /></span>
            <input
              type="search"
              placeholder="Rechnungsnummer oder Kunde…"
              value={suche}
              onChange={e => setSuche(e.target.value)}
              className={`${inputClass} min-h-[40px] pl-9 ${suche.trim() ? filterAktiv : ''}`}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:flex-1 xl:flex-none">
            <Select value={status} onChange={e => setStatus(e.target.value)} className={`min-h-[40px] xl:w-40 ${status ? filterAktiv : ''}`}>
              <option value="">Alle Status</option>
              {RECHNUNG_STATUS.map(s => <option key={s} value={s}>{RECHNUNG_STATUS_LABEL[s]}</option>)}
              <option value="UEBERFAELLIG">Überfällig</option>
            </Select>
            <Select value={jahr} onChange={e => setJahr(e.target.value)} className={`min-h-[40px] xl:w-36 ${jahr ? filterAktiv : ''}`}>
              <option value="">Alle Jahre</option>
              {jahre.map(j => <option key={j} value={j}>{j}</option>)}
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-100 pt-3">
          <VorschauSchalter aktiv={vorschau} onAktiv={setVorschau} />
          {vorschau && <ZeitraumAuswahl zeitraum={zeitraum} onZeitraum={setZeitraum} />}
          <FilterStand
            treffer={gefiltert.length} gesamt={items.length} einheit="Rechnungen"
            aktiv={!!(status || jahr || suche.trim())}
            onZuruecksetzen={() => { setStatus(''); setJahr(''); setSuche(''); }}
          />
        </div>
      </Card>

      {/* Kontoauszug: vorgemerkte (nur Seite 1) über „Heute“, darunter die echten Rechnungen. */}
      {gefiltert.length === 0 && (!vorschau || vorgemerktGefiltert.length === 0) ? (
        <Card>
          {vorschau && <VorschauKopf daten={vorgemerkt} liste={vorgemerktGefiltert} />}
          {items.length === 0 ? (
            <EmptyState
              icon={<Icon.Doc />}
              title="Noch keine Rechnungen"
              hint="Zu jedem Jahrestag einer Vertragsunterschrift entsteht automatisch ein Entwurf. Einzelne Leistungen lassen sich jederzeit von Hand abrechnen."
              action={<Button onClick={() => setSchnell(true)} icon={<Icon.Plus className="h-4 w-4" />}>Schnellrechnung</Button>}
            />
          ) : (
            <EmptyState
              icon={<Icon.Search className="h-5 w-5" />}
              title="Keine Rechnungen für diesen Filter"
              action={<Button variant="secondary" onClick={() => { setStatus(''); setJahr(''); setSuche(''); }}>Filter zurücksetzen</Button>}
            />
          )}
        </Card>
      ) : (
        <RechnungsZeitachse
          rechnungen={sichtbar}
          vorgemerkte={vorschau ? vorgemerktGefiltert : []}
          mitHeute={vorschau}
          kopf={vorschau ? <VorschauKopf daten={vorgemerkt} liste={vorgemerktGefiltert} /> : undefined}
          onBezahlt={alsBezahlt}
          bezahltLaeuft={bezahltLaeuft}
        />
      )}
      {Ende}
    </div>
  );
};

export default Rechnungen;
